import React from "react";
import { RouteProps, Navigate, RouteObject } from "react-router-dom";
import { hasPermission } from "utility/accessibility/hasPermission";

// Authentication Pages
import SignIn from "pages/authentication/SignIn";
import SetNewPasswordPage from "pages/authentication/SetNewPasswordPage";
import ResetPasswordPage from "pages/authentication/ResetPasswordPage";
import ForgotPassword from "pages/authentication/ForgotPassword";

// Authorization Pages
import UnAuthorized from "pages/authorization/unAuthorized/unAuthorized";
import NotFound from "pages/authorization/not-found/not-found";

// Localization Pages
import EmailPage from "pages/localization/email/EmailPage";
import SystemLanguages from "pages/localization/system-languages/SystemLanguages";
import Translations from "pages/localization/translations/Translations";

// User Management Pages
import UsersPage from "pages/users/UsersPage";
import CreateUserPage from "pages/users/CreateUserPage";
import UpdateUserPage from "pages/users/UpdateUserPage";
import UserGroupPage from "pages/user-groups/UserGroupPage";

// Settings and Profile Pages
import Settings from "pages/settings/Settings";

// Organization Pages
import { OrganizationsPage } from "pages/organizations/OrganizationsPage";

// Client Pages
import { ClientsPage } from "pages/clients/ClientsPage";

// Task Pages
import { TasksPage } from "pages/tasks/TasksPage";

// Project Pages
import ProjectsPage from "pages/projects/ProjectsPage";
import ProjectDetailsPage from "pages/projects/ProjectDetailsPage";

// Contractor Pages
import ContractorsPage from "pages/contractors/ContractorsPage";
import { ContractorDetailsPage } from "pages/contractors/ContractorDetailsPage";

// Supplier Pages
import SuppliersPage from "pages/suppliers/SuppliersPage";

// Miscellaneous Pages
import { Dashboard } from "pages/dashboard/Dashboard";
import { UnitCategoriesPage } from "pages/unit-categories/UnitCategoriesPage";
import { UnitCategoryDetailsPage } from "pages/unit-categories/UnitCategoryDetailsPage";
import { UnitDetailsPage } from "pages/unit-categories/components/UnitDetailsPage";
import { ClientDetailsPage } from "pages/clients/ClientDetailsPage";
import DealsPage from "pages/deals/DealsPage";
import DealFormPage from "pages/deals/components/DealFormPage";
import DealDetailsPage from "pages/deals/DealDetailsPage";
import DealUpdatePage from "pages/deals/DealUpdatePage";
import DelayedPayments from "pages/payments/DelayedPayments";
import { ContractorPaymentsPage } from "pages/contractors/ContractorPaymentsPage";
import { ContractorPaymentsSummaryPage } from "pages/contractors/ContractorPaymentsSummaryPage";
import PrivacyPolicyPage from "pages/public/privacy-policy/PrivacyPolicyPage";
import SupportPage from "pages/public/support/SupportPage";
import SupplierDetailsPage from "pages/suppliers/SupplierDetailsPage";
import SupplierPaymentsPage from "pages/suppliers/SupplierPaymentsPage";
import CreateSupplierPaymentPage from "pages/suppliers/CreateSupplierPaymentPage";
import UpcomingPayments from "pages/payments/UpcomingPayments";
import DebtCollectionPage from "pages/debt-collection/DebtCollectionPage";

export type CustomRouteProps = {
	hasAccess: () => boolean;
} & RouteProps;

export const MainRoutes: CustomRouteProps[] = [
	// Dashboard
	{
		index: true,
		path: "/",
		element: <Dashboard />,
		hasAccess: () => true,
	},
	{
		index: false,
		path: "/dashboard",
		element: <Dashboard />,
		hasAccess: () => true,
	},

	// Localization
	{
		path: "/languages",
		element: <SystemLanguages />,
		hasAccess: () => hasPermission("languages:list"),
	},
	{
		path: "/translations",
		element: <Translations />,
		hasAccess: () => hasPermission("translations:list"),
	},
	{
		path: "/email",
		element: <EmailPage />,
		hasAccess: () => hasPermission("settings:manage-email"),
	},

	// User Management
	{
		index: false,
		path: "/users",
		element: <UsersPage />,
		hasAccess: () => hasPermission("users:list"),
	},
	{
		index: false,
		path: "/users/create",
		element: <CreateUserPage />,
		hasAccess: () => hasPermission("users:add"),
	},
	{
		index: true,
		path: "/users/:id/edit",
		element: <UpdateUserPage />,
		hasAccess: () => hasPermission("users:add"),
	},
	{
		index: false,
		path: "/user-groups",
		element: <UserGroupPage />,
		hasAccess: () => hasPermission("user-groups:list"),
	},

	// Settings
	{
		index: false,
		path: "/profile",
		element: <Settings />,
		hasAccess: () => true,
	},

	// Organizations
	{
		path: "/organizations",
		element: <OrganizationsPage />,
		hasAccess: () => hasPermission("organizations:list"),
	},

	// Clients
	{
		path: "/clients",
		element: <ClientsPage />,
		hasAccess: () => hasPermission("clients:list"),
	},
	{
		path: "/clients/:id",
		element: <ClientDetailsPage />,
		hasAccess: () => hasPermission("clients:view"),
	},

	// Tasks
	{
		path: "/tasks",
		element: <TasksPage />,
		hasAccess: () => true, //hasPermission('tasks:list'),
	},

	// Projects
	{
		path: "/projects",
		element: <ProjectsPage />,
		hasAccess: () => hasPermission("projects:list"),
	},
	{
		path: "/projects/:id",
		element: <ProjectDetailsPage />,
		hasAccess: () => hasPermission("projects:view"),
	},

	// Contractors
	{
		path: "/contractors",
		element: <ContractorsPage />,
		hasAccess: () => hasPermission("contractors:list"),
	},
	{
		path: "/contractors/:id",
		element: <ContractorDetailsPage />,
		hasAccess: () => hasPermission("contractors:view"),
	},

	// Suppliers
	{
		path: "/suppliers",
		element: <SuppliersPage />,
		hasAccess: () => hasPermission("suppliers:list"),
	},
	{
		path: "/suppliers/:id",
		element: <SupplierDetailsPage />,
		hasAccess: () => hasPermission("suppliers:view"),
	},
	{
		path: "/supplier-payments",
		element: <SupplierPaymentsPage />,
		hasAccess: () => hasPermission("suppliers:manage-payments"),
	},
	{
		path: "/supplier-payments/create",
		element: <CreateSupplierPaymentPage />,
		hasAccess: () => hasPermission("suppliers:manage-payments"),
	},
	{
		path: "/supplier-payments/:id/edit",
		element: <CreateSupplierPaymentPage />,
		hasAccess: () => hasPermission("suppliers:manage-payments"),
	},

	// Contractor Payments
	{
		path: "/contractors/:contractorId/payments",
		element: <ContractorPaymentsPage />,
		hasAccess: () => hasPermission("contractors:manage-payments"),
	},
	{
		path: "/contractors/:contractorId/payments/summary",
		element: <ContractorPaymentsSummaryPage />,
		hasAccess: () => hasPermission("contractors:manage-payments"),
	},

	// Unit Categories
	{
		path: "/buildings",
		element: <UnitCategoriesPage />,
		hasAccess: () => hasPermission("unit-categories:list"),
	},
	{
		path: "/unit-categories/:id",
		element: <UnitCategoryDetailsPage />,
		hasAccess: () => hasPermission("unit-categories:view"),
	},

	// Units
	{
		path: "/units/:id",
		element: <UnitDetailsPage />,
		hasAccess: () => hasPermission("units:view"),
	},

	// Deals
	{
		path: "/deals",
		element: <DealsPage />,
		hasAccess: () => hasPermission("deals:list"),
	},
	{
		path: "/deals/new",
		element: <DealFormPage />,
		hasAccess: () => hasPermission("deals:create"),
	},
	{
		path: "/deals/:id",
		element: <DealDetailsPage />,
		hasAccess: () => hasPermission("deals:view"),
	},
	{
		path: "/deals/:id/edit",
		element: <DealUpdatePage />,
		hasAccess: () => hasPermission("deals:update"),
	},

	// Payments
	{
		path: "/payments/upcoming",
		element: <UpcomingPayments />,
		hasAccess: () => hasPermission("deals:list"),
	},
	{
		path: "/payments/delayed",
		element: <DelayedPayments />,
		hasAccess: () => hasPermission("deals:list"),
	},

	// Debt Collection
	{
		path: "/debt-collection",
		element: <DebtCollectionPage />,
		hasAccess: () => true, //hasPermission("debt-collection:manage"),
	},

	// Authorization
	{
		index: false,
		path: "/unauthorized",
		element: <UnAuthorized />,
		hasAccess: () => true,
	},
	{
		index: false,
		path: "*",
		element: <NotFound />,
		hasAccess: () => true,
	},
];

export const AuthRoutes: RouteProps[] = [
	{
		index: false,
		path: "/login",
		element: <SignIn />,
	},
	{
		index: false,
		path: "/set-new-password",
		element: <SetNewPasswordPage />,
	},
	{
		index: false,
		path: "/reset-password",
		element: <ResetPasswordPage />,
	},
	{
		index: false,
		path: "/forgot-password",
		element: <ForgotPassword />,
	},
];

export const PublicRoutes: RouteProps[] = [
	{
		index: false,
		path: "/privacy-policy",
		element: <PrivacyPolicyPage />,
	},
	{
		index: false,
		path: "/support",
		element: <SupportPage />,
	},
];
